import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';

import OrderComment from 'pages/Shipments/ActionForms/OrderComment';
import OrderStatuses from 'pages/Shipments/ActionForms/OrderStatuses';
import PackingStatuses from 'pages/Shipments/ActionForms/PackingStatuses';
import PrintOrders from 'pages/Shipments/PrintOrders/PrintOrders';

import { ORDERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faPrint } from '@fortawesome/pro-solid-svg-icons';

import { CustomerDetails } from './CustomerDetails/CustomerDetails';
import OrderDetails from './OrderDetails/OrderDetails';
import { ShippingDetails } from './ShippingDetails/ShippingDetails';

/**
 * @author Burak Ak
 */

const ViewOrderForm = NiceModal.create(({ id, modalName, apiSource }) => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const { closeModal } = useModalRouter();
	const modal = useModal();
	const axios = useAxiosPrivate();
	const { showError } = useAlert();
	const isAuthorized = useAccessControl();

	const fillForm = useCallback(
		async () =>
			await axios
				.post(apiSource || ORDERS.GET_ORDER_BY_ID, { ID: Number(id) })
				.then((response) => {
					const { data, status } = response;

					if (status === 200) {
						return {
							// Customer Details
							firstName: data.customerDetails.firstName ?? null,
							lastName: data.customerDetails.lastName ?? null,
							warehouseID: data.customerDetails.warehouseID ?? null,
							branchID: data.customerDetails.branchID ?? null,
							email: data.customerDetails.email ?? null,
							phone: data.customerDetails.phone ?? null,
							address: {
								...data.customerDetails.address
							},
							// Order Details
							orderID: data.orderDetails.orderID ?? null,
							orderDate: data.orderDetails.orderDate ?? null,
							orderComments: data.orderDetails.orderComments ?? null,
							brandID: data.customerDetails.brandID ?? null,
							paymentMethod: data.orderDetails.paymentMethod ?? null,
							currency: data.orderDetails.currency ?? null,
							grandTotal: data.orderDetails.grandTotal ?? null,
							totalDue: data.orderDetails.totalDue ?? null,
							packageCount: data.orderDetails.packageCount ?? null,
							refNo: data.orderDetails.refNo ?? null,
							weight: data.orderDetails.weight ?? null,

							// Shipping Details
							deliveryCompany: data.shipmentDetails.deliveryCompany ?? null,
							shipmentID: data.shipmentDetails.shipmentID ?? null,
							deliverySlotDate: data.shipmentDetails.deliverySlotDate ?? null,
							deliverySlotFrom: data.shipmentDetails.deliverySlotFrom ?? null,
							deliverySlotTo: data.shipmentDetails.deliverySlotTo ?? null,
							shippingDescription: data.shipmentDetails.shippingDescription ?? null,
							drivingDistance: data.shipmentDetails.drivingDistance ?? null,
							trackingURL: data.shipmentDetails.trackingURL ?? null,

							cancelComment: data.shipmentDetails.cancelComment ?? null,
							cancelReason: data.shipmentDetails.cancelReason ?? null,
							onHoldComment: data.shipmentDetails.onHoldComment ?? null,
							onHoldReason: data.shipmentDetails.onHoldReason ?? null,
							returnReason: data.shipmentDetails.returnReason ?? null,
							orderDocs: data.orderDocs ?? null,
							// Status History
							status: data.shipmentDetails.status ?? null,
							// Item details
							products: data.itemsDetails,
							boxes: data.boxes,
							manuallyAdded: data.orderDetails.manuallyAdded ?? true
						};
					} else {
						AppLogger.error(response);
						showError();
					}
				})
				.catch((error) => {
					if (error.response?.data.code) {
						AppLogger.error(
							`ViewOrderForm:fillForm: ${error.response?.data.code} ${error.response?.data.message} ${error.response?.data.description}`
						);
						showError({
							header: 'error',
							message: error.response?.data.code
						});
						handleCloseModal();
					}
					AppLogger.error(error);
					showError();
				}),
		[]
	);

	// Form setup using react-hook-form
	const formMethods = useForm({
		defaultValues: fillForm
	});

	const { watch } = formMethods;

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	// Check if order id is provided
	useLayoutEffect(() => {
		if (!id) {
			showError({
				header: 'error',
				message: 'alert.orderNotSelected'
			});
			handleCloseModal();
		}
	}, [id]);

	const tabs = useMemo(
		() => [
			{
				label: 'customerDetails',
				component: () => <CustomerDetails formMode="view" />
			},
			{
				label: 'orderDetails',
				component: () => <OrderDetails formMode="view" otoOrderID={id} />
			},

			{
				label: 'shippingDetails',
				component: () => <ShippingDetails formMode="view" />
			},
			...(isAuthorized(['WMSModule', 'WMSAdvanceModule'])
				? [
						{
							label: 'packingStatusHistory',
							component: () => <PackingStatuses formMode="view" ID={id} />
						}
					]
				: []),
			{
				label: 'statusHistory',
				component: () => <OrderStatuses formMode="view" ID={id} />
			},
			{
				label: 'ViewOrderForm.comments',
				component: () => <OrderComment formMode="view" ID={id} />
			}
		],
		[]
	);

	return (
		<Modal
			fullwidth
			maxWidth="xx-large"
			name={modalName}
			className="modal-viewOrderForm"
			minimizeButton={(show, close) => (
				<MinimizeButton onShow={show} onClose={close} label="viewOrder" tooltip={watch('orderID')} />
			)}>
			<ModalTitle>
				<Typography variant="p1">
					{t('viewOrder')} (<strong>{watch('orderID')}</strong>)
				</Typography>
			</ModalTitle>

			<ModalContent style={{ width: '100%' }}>
				<Form borderless formMethods={formMethods}>
					<Tabs data={tabs} />
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					icon={faPrint}
					label="print"
					variant="outline"
					onClick={() => {
						NiceModal.show(PrintOrders, {
							ids: [Number(id)]
						});
					}}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ViewOrderForm;
